import {
  addConversationParticipants,
  AdminSpinner,
  AdminSwitch,
  Box,
  Button,
  ChatConfirmModal,
  ConversationCandidateResponse,
  Disclaimer,
  Div,
  groupMemberTeam,
  H5,
  Icon,
  isGroupConversation,
  leaveConversation,
  lightTheme,
  ModalPortal,
  muteConversation,
  PlusIcon,
  presignConversationGroupImage,
  putToPresignedUrl,
  queryKeys,
  removeConversationParticipant,
  renderRole,
  SimpleLocationEntity,
  SuccessToast,
  TrashIconRed,
  updateConversationGroup,
  useAuthenticatedAuthUser,
  useBusyAction,
  UserRole,
} from '@vgrubs/components';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { AddMembersModal } from './AddMembersModal';
import { CandidateLocationFilter } from './components/CandidateLocationFilter';
import { ConversationPersonRow } from './components/ConversationPersonRow';
import { CONVERSATION_MODAL_HEIGHT } from './conversationModal';
import { GroupIdentityFields } from './components/GroupIdentityFields';
import {
  useConversationCandidates,
  useConversationDetail,
} from './hooks/useConversationQueries';
import { openUserDetails } from './userDetailsPath';

/**
 * Everything about a conversation that is not a message: who is in it, what it
 * is called, and whether it is allowed to buzz you.
 *
 * Mute is here for every type, because every member can silence any thread they
 * are in. The rest - rename, photo, add and remove - only makes sense for a
 * group, and only the owner (or an admin) may do it, so those controls are
 * gated rather than shown-and-rejected.
 *
 * Membership changes write a system message into the thread, which is how the
 * other members find out; the socket's `conversation_updated` is what brings
 * this panel back in line.
 */

/** Far enough out to read as "off" without inventing an indefinite flag. */
const MUTE_FOREVER = '2099-01-01T00:00:00.000Z';

/**
 * `PlusIcon` is the filled red disc used everywhere a row gets added - custom
 * prices, modifier groups. It opens the picker in a modal of its own; it used
 * to unfold one inline and rotated into a close button while open, which is
 * where the transition here comes from.
 */
const AddToggle = styled(Icon)`
  transition: ${({ theme }) => theme.transitions.ease200ms};
`;

/**
 * Marks the owner's row, in the slot every other row keeps its delete button
 * in.
 *
 * That slot is where the eye goes looking for "what can I do to this person",
 * and on the owner the honest answer is nothing - so it says why, rather than
 * leaving a gap that reads as a button that failed to render. Grey and quiet
 * on purpose: it is a label, not the red control it stands in for.
 *
 * Being the owner also stopped taking the subtitle's place: the line under a
 * name is where you look for who someone IS, and losing "Admin" there to a
 * word about this one group made the owner the only member whose role the list
 * would not tell you.
 */
const OwnerPill = styled.span`
  flex: none;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
  background: ${({ theme }) => theme.gray};
  color: ${({ theme }) => theme.darkGray};
`;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  conversationId: number | null;
  /** Called after leaving, so the page can clear the open thread. */
  onLeft: () => void;
};

export const ConversationDetailsModal = ({
  isOpen,
  onClose,
  conversationId,
  onLeft,
}: Props) => {
  const queryClient = useQueryClient();
  const user = useAuthenticatedAuthUser();

  const [title, setTitle] = useState('');
  const [image, setImage] = useState<File | Blob | string | null>(null);
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState('');
  /** The member a trash click is waiting on confirmation for. */
  const [removing, setRemoving] = useState<{
    userId: number;
    name: string;
  } | null>(null);
  /** Whether Leave Group is waiting on its confirmation. */
  const [leaving, setLeaving] = useState(false);
  /** The add-members picker's location filter, merchant groups only. */
  const [location, setLocation] = useState<SimpleLocationEntity | null>(null);

  const { data: conversation, isLoading } = useConversationDetail({
    conversationId,
    enabled: isOpen,
  });

  /**
   * What is no longer true after a write here: this panel, the row in the list,
   * and the conversation itself.
   *
   * The thread is in that list because every change made here also writes a
   * system message into it ("You added X"). Waiting for the socket to echo an
   * action THIS tab just performed is the wrong dependency - the result of a
   * button should not depend on the server choosing to broadcast it back to the
   * person who pressed it. The socket handler does the same for everyone else
   * in the group, and React Query folds the two into one request when they
   * overlap.
   */
  const refresh = () => {
    if (conversationId === null) return;
    queryClient.invalidateQueries({
      queryKey: queryKeys.conversations.detail(conversationId),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.conversations.messages(conversationId),
    });
    queryClient.resetQueries({ queryKey: queryKeys.conversations.inboxAll });
  };

  const { busy, run: runAction } = useBusyAction(refresh);
  /** Nothing here is meaningful without a conversation to act on. */
  const run = (action: () => Promise<unknown>, message?: string) =>
    conversationId === null ? undefined : runAction(action, message);

  /* Load the current values into the draft whenever a fresh detail arrives -
     including after the socket says somebody else renamed it. */
  useEffect(() => {
    if (!conversation) return;
    setTitle(conversation.title ?? '');
    setImage(conversation.imageUrl ?? null);
  }, [conversation]);

  useEffect(() => {
    if (!isOpen) {
      setAdding(false);
      setQuery('');
      setLeaving(false);
      setRemoving(null);
      setLocation(null);
    }
  }, [isOpen]);

  const isGroup = isGroupConversation(conversation?.type);
  /* The kind with restaurants in it, which draws its new members from a
     wider pool - see the candidates below. */
  const isMerchantGroup = conversation?.type === 'merchantGroup';
  const me = conversation?.participants.find(
    (participant) => participant.userId === user.id
  );
  /**
   * Admin and support can manage any group - the backend opened rename, add
   * and remove to support along with creation - and otherwise it is the
   * owner's to run. Checked here so the buttons are simply absent rather than
   * failing on save. Marketing is a participant and nothing more.
   */
  const canManage =
    isGroup &&
    (user.role === UserRole.admin ||
      user.role === UserRole.support ||
      me?.participantRole === 'owner');

  const muted =
    !!conversation?.mutedUntil &&
    new Date(conversation.mutedUntil).getTime() > Date.now();

  /* New members come from the pool the group was made from - colleagues and
     sales for an internal one, restaurants too for a merchant group - which
     the backend checks again on add. */
  const { data: candidates } = useConversationCandidates({
    query,
    pool: isMerchantGroup ? 'merchantGroup' : 'group',
    locationId: isMerchantGroup ? location?.id ?? null : null,
    enabled: isOpen && adding,
  });

  /** Somebody already in the group is not a candidate to add to it. */
  const addable = useMemo(() => {
    const existing = new Set(
      (conversation?.participants ?? []).map(
        (participant) => participant.userId
      )
    );
    return (candidates ?? []).filter(
      (candidate: ConversationCandidateResponse) =>
        !existing.has(candidate.userId)
    );
  }, [candidates, conversation]);

  const saveTitle = () => {
    const next = title.trim();
    if (!next || next === conversation?.title) return;
    run(
      () => updateConversationGroup(conversationId as number, { title: next }),
      'Group renamed.'
    );
  };

  /**
   * Same presign-and-PUT path as a chat attachment: group images are stored by
   * key, not uploaded through the API. Kept inline rather than sharing the
   * create form's `uploadGroupImage` because this one also has to handle the
   * photo being removed, which has no upload at all.
   */
  const saveImage = async (next: File | Blob | string | null) => {
    setImage(next);
    if (!next) {
      run(
        () =>
          updateConversationGroup(conversationId as number, { imageKey: null }),
        'Group photo removed.'
      );
      return;
    }
    if (typeof next === 'string') return; // Unchanged - this is the existing url.

    const file = next as File;
    run(async () => {
      const { data } = await presignConversationGroupImage({
        mime: file.type,
        size: file.size,
        name: file.name,
      });
      await putToPresignedUrl({
        url: data.data.uploadUrl,
        headers: data.data.headers,
        body: file,
      });
      await updateConversationGroup(conversationId as number, {
        imageKey: data.data.key,
      });
    }, 'Group photo updated.');
  };

  /* Confirmed by then - the button below only arms it. Nothing closes the
     confirmation on failure: the group is still there to leave, and `run` has
     already said why it did not happen. */
  const leave = () =>
    run(async () => {
      await leaveConversation(conversationId as number);
      SuccessToast('You left the group.');
      setLeaving(false);
      onLeft();
      onClose();
    });

  const toggleMute = () =>
    run(() =>
      muteConversation(conversationId as number, {
        mutedUntil: muted ? null : MUTE_FOREVER,
      })
    );

  return (
    <ModalPortal
      isOpen={isOpen}
      closeModal={onClose}
      header={isGroup ? 'Group Details' : 'Conversation Details'}
      width="520px"
      minHeight={CONVERSATION_MODAL_HEIGHT}
      maxHeight={CONVERSATION_MODAL_HEIGHT}
    >
      {isLoading || !conversation ? (
        // Fills the pinned height, so the spinner sits in the middle of the
        // modal rather than parked at the top of an empty box.
        <Div $flex $center $height="100%">
          <AdminSpinner small $noBackground />
        </Div>
      ) : (
        <>
          {isGroup && (
            <GroupIdentityFields
              title={title}
              onTitleChange={setTitle}
              onTitleBlur={saveTitle}
              image={image}
              onImageChange={saveImage}
              onImageDelete={() => saveImage(null)}
              canManage={canManage}
            />
          )}

          <Box $mb="20" $flex $alignCenter $between $gap="16px">
            <Div>
              <H5 $mb="4">Mute</H5>
              <Disclaimer $m="0" $color={lightTheme.lightGray}>
                No push from this conversation. A mention still comes through.
              </Disclaimer>
            </Div>
            <AdminSwitch
              checked={muted}
              disabled={busy}
              onChange={toggleMute}
            />
          </Box>

          <Div $flex $alignCenter $between $mb="8">
            <H5 $m="0">Members ({conversation.participants.length})</H5>
            {canManage && (
              <AddToggle
                $pointer
                aria-label="Add members"
                onClick={() => setAdding(true)}
              >
                <PlusIcon />
              </AddToggle>
            )}
          </Div>

          {conversation.participants.map((participant) => (
            <ConversationPersonRow
              key={participant.userId}
              name={`${participant.name}${
                participant.userId === user.id ? ' (you)' : ''
              }`}
              /* In a merchant group, which side somebody is on comes first:
                 a role alone ("Manager") does not say it is a restaurant's. */
              subtitle={
                isMerchantGroup
                  ? [
                      groupMemberTeam(participant.role),
                      renderRole(participant.role),
                    ]
                      .filter(Boolean)
                      .join(' · ')
                  : renderRole(participant.role)
              }
              // Opens in a new tab - see `openUserDetails`.
              onNameClick={() =>
                openUserDetails(participant.userId, participant.role)
              }
              trailing={
                /* The owner cannot be removed by anyone - they leave, which
                   hands the group its own system message - so their row says
                   so instead of offering a control that would be refused. */
                participant.participantRole === 'owner' ? (
                  <OwnerPill>Group Owner</OwnerPill>
                ) : canManage && participant.userId !== user.id ? (
                  <Div $pointer onClick={() => setRemoving(participant)}>
                    <TrashIconRed />
                  </Div>
                ) : undefined
              }
            />
          ))}

          {isGroup && (
            <Button
              $gray
              $selected
              $w="100%"
              $wSM="100%"
              disabled={busy}
              onClick={() => setLeaving(true)}
              style={{ marginTop: 20 }}
            >
              Leave Group
            </Button>
          )}
        </>
      )}

      {/* Rendered inside this modal's tree but portalled above it, so adding
          someone lands you straight back on the member list it changed. */}
      <AddMembersModal
        isOpen={adding}
        onClose={() => {
          setAdding(false);
          setQuery('');
          setLocation(null);
        }}
        query={query}
        onQueryChange={setQuery}
        filter={
          isMerchantGroup && (
            <CandidateLocationFilter
              location={location}
              onChange={setLocation}
            />
          )
        }
        candidates={addable}
        busy={busy}
        onAdd={async (picked) => {
          const added = await run(
            () =>
              addConversationParticipants(conversationId as number, {
                userIds: picked.map((candidate) => candidate.userId),
              }),
            picked.length === 1
              ? `${picked[0].name} added.`
              : `${picked.length} members added.`
          );
          // Stays open on failure, with the picks intact, so the whole
          // selection does not have to be made again.
          if (added) {
            setAdding(false);
            setQuery('');
            setLocation(null);
          }
        }}
      />

      <ChatConfirmModal
        isOpen={!!removing}
        confirmLabel="Remove"
        message={
          <>
            Remove <b>{removing?.name}</b> from this group? They lose access to
            the conversation and everything in it.
          </>
        }
        busy={busy}
        onClose={() => setRemoving(null)}
        onConfirm={async () => {
          if (!removing) return;
          const removed = await run(
            () =>
              removeConversationParticipant(
                conversationId as number,
                removing.userId
              ),
            `${removing.name} removed.`
          );
          if (removed) setRemoving(null);
        }}
      />

      <ChatConfirmModal
        isOpen={leaving}
        confirmLabel="Leave"
        message={
          <>
            Leave <b>{conversation?.title || 'this group'}</b>? You stop
            receiving its messages, and only a member can add you back.
          </>
        }
        busy={busy}
        onClose={() => setLeaving(false)}
        onConfirm={leave}
      />
    </ModalPortal>
  );
};
